/**
 * The raider as a jointed plastic toy, animated procedurally — the instant-on fallback.
 *
 * The shipped raider is Kenney's skinned character (`character.ts`), but this stays, and stays
 * first, for two reasons:
 *  1. Payload. It costs zero bytes, so the game is playable on the first frame and stays playable
 *     if the GLB never arrives (bad network, a portal that rehosts assets badly).
 *  2. Comedy. The signature moment is being launched across the room, and a rigid toy that tumbles
 *     as one piece reads *funnier* and never wedges, unlike a ragdoll. `flung` is one rotation on
 *     the whole body, driven by the sim's `tumble` accumulator — the skinned figure copies that
 *     behaviour rather than replacing it.
 *
 * Game code should call `createFigure()` from `character.ts` instead of constructing either class
 * directly; it hands back this figure now and swaps in the skinned one when it loads.
 */

import * as THREE from 'three'

import type { RaiderState } from '../sim/types'
import { SIM } from '../sim/config'
import { chestGeometry } from './geometry'
import { facet, outlineMaterial, toonMaterial } from './toon'

/** What the game modes need from a raider, whichever representation is live. */
export interface Figure {
  readonly root: THREE.Object3D
  update(state: RaiderState, dt: number): void
  dispose(): void
}

export interface FigurePalette {
  skin: number
  shirt: number
  pants: number
  hair: number
}

export const RAIDER_PALETTE: FigurePalette = {
  skin: 0xf5cfa8,
  shirt: 0xe4694f,
  pants: 0x4b6b8f,
  hair: 0x4a3324,
}

export const GHOST_PALETTE: FigurePalette = {
  skin: 0xbfc9d4,
  shirt: 0x9aa7b6,
  pants: 0x7d8996,
  hair: 0x6f7986,
}

function part(
  geo: THREE.BufferGeometry,
  color: number,
  outline = false,
): THREE.Group {
  const g = new THREE.Group()
  const mesh = new THREE.Mesh(facet(geo), toonMaterial(color))
  mesh.castShadow = true
  g.add(mesh)
  if (outline) {
    const shell = new THREE.Mesh(mesh.geometry, outlineMaterial())
    shell.scale.setScalar(1.09)
    g.add(shell)
  }
  return g
}

export class ToyFigure implements Figure {
  readonly root = new THREE.Group()
  private tilt = new THREE.Group()
  private body = new THREE.Group()
  private legL: THREE.Group
  private legR: THREE.Group
  private armL: THREE.Group
  private armR: THREE.Group
  private torso: THREE.Group
  private head: THREE.Group
  private held: THREE.Group
  private stride = 0
  private geometries: THREE.BufferGeometry[] = []

  constructor(palette: FigurePalette = RAIDER_PALETTE, opts: { ghost?: boolean } = {}) {
    this.root.add(this.tilt)
    this.tilt.add(this.body)

    const track = <T extends THREE.BufferGeometry>(g: T): T => {
      this.geometries.push(g)
      return g
    }

    // Legs pivot at the hip so a swing looks like a stride, not a slide.
    this.legL = part(track(new THREE.BoxGeometry(0.16, 0.3, 0.17).translate(0, -0.15, 0)), palette.pants)
    this.legR = part(track(new THREE.BoxGeometry(0.16, 0.3, 0.17).translate(0, -0.15, 0)), palette.pants)
    this.legL.position.set(-0.11, 0.32, 0)
    this.legR.position.set(0.11, 0.32, 0)

    this.torso = part(track(new THREE.BoxGeometry(0.38, 0.36, 0.24).translate(0, 0.18, 0)), palette.shirt, !opts.ghost)
    this.torso.position.y = 0.32

    this.armL = part(track(new THREE.BoxGeometry(0.1, 0.28, 0.12).translate(0, -0.14, 0)), palette.skin)
    this.armR = part(track(new THREE.BoxGeometry(0.1, 0.28, 0.12).translate(0, -0.14, 0)), palette.skin)
    this.armL.position.set(-0.24, 0.62, 0)
    this.armR.position.set(0.24, 0.62, 0)

    this.head = part(track(new THREE.BoxGeometry(0.3, 0.28, 0.28).translate(0, 0.14, 0)), palette.skin, !opts.ghost)
    this.head.position.y = 0.68
    const hair = new THREE.Mesh(
      facet(track(new THREE.BoxGeometry(0.32, 0.1, 0.3).translate(0, 0.31, -0.01))),
      toonMaterial(palette.hair),
    )
    hair.castShadow = true
    this.head.add(hair)

    // The loot, held in front. Visible carrying state is what makes the exit
    // legible to a spectator three seconds into a clip.
    const chest = chestGeometry()
    this.geometries.push(chest.body, chest.accent)
    this.held = new THREE.Group()
    const cb = new THREE.Mesh(chest.body, toonMaterial(0xb98a52))
    const ca = new THREE.Mesh(chest.accent, toonMaterial(0xffd166))
    this.held.add(cb, ca)
    this.held.scale.setScalar(0.62)
    this.held.position.set(0, 0.34, 0.28)
    this.held.visible = false

    this.body.add(this.legL, this.legR, this.torso, this.armL, this.armR, this.head, this.held)

    if (opts.ghost) {
      this.root.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.castShadow = false
          o.material = (o.material as THREE.MeshToonMaterial).clone()
          const m = o.material as THREE.MeshToonMaterial
          m.transparent = true
          m.opacity = 0.45
          m.depthWrite = false
        }
      })
    }
  }

  /** Drive the pose from a sim (or replay) state. `dt` only paces the stride cycle. */
  update(state: RaiderState, dt: number): void {
    this.root.position.set(state.x, state.y - SIM.raider.standY, state.z)
    this.body.rotation.y = state.yaw
    this.held.visible = state.carrying

    if (state.pose === 'flung') {
      // Whole-body tumble around the axis perpendicular to travel.
      const travel = Math.atan2(state.vx, state.vz)
      this.tilt.rotation.set(0, 0, 0)
      this.tilt.rotation.y = travel
      this.tilt.rotation.x = state.tumble
      this.tilt.position.y = 0.36
      this.body.rotation.y = 0
      this.splay()
      return
    }

    this.tilt.rotation.set(0, 0, 0)
    this.tilt.position.y = 0

    switch (state.pose) {
      case 'run': {
        this.stride += dt * (4 + state.speed * 2.2)
        const swing = Math.sin(this.stride) * 0.85
        this.legL.rotation.x = swing
        this.legR.rotation.x = -swing
        this.armL.rotation.x = -swing * 0.7
        this.armR.rotation.x = swing * 0.7
        this.torso.rotation.x = 0.12
        this.body.position.y = Math.abs(Math.sin(this.stride)) * 0.035
        this.head.rotation.z = Math.sin(this.stride * 0.5) * 0.06
        break
      }
      case 'air': {
        this.legL.rotation.x = -0.5
        this.legR.rotation.x = 0.3
        this.armL.rotation.x = -1.5
        this.armR.rotation.x = -1.5
        this.torso.rotation.x = -0.1
        this.body.position.y = 0
        break
      }
      case 'stuck': {
        this.stride += dt * 2.2
        const strain = Math.sin(this.stride) * 0.3
        this.legL.rotation.x = strain
        this.legR.rotation.x = -strain
        this.armL.rotation.x = -0.8
        this.armR.rotation.x = -0.8
        this.torso.rotation.x = 0.3
        this.body.position.y = 0
        break
      }
      default: {
        this.stride += dt
        const breathe = Math.sin(this.stride * 1.6) * 0.02
        this.legL.rotation.x = 0
        this.legR.rotation.x = 0
        this.armL.rotation.x = breathe * 2
        this.armR.rotation.x = -breathe * 2
        this.torso.rotation.x = 0
        this.body.position.y = breathe
        this.head.rotation.z = 0
        break
      }
    }
  }

  /** Limbs out, the universal shape of "this is not going well". */
  private splay(): void {
    this.legL.rotation.x = -0.9
    this.legR.rotation.x = 0.9
    this.armL.rotation.x = -2.1
    this.armR.rotation.x = -2.1
    this.armL.rotation.z = 0.5
    this.armR.rotation.z = -0.5
    this.torso.rotation.x = -0.2
    this.body.position.y = 0
  }

  dispose(): void {
    for (const g of this.geometries) g.dispose()
    this.geometries = []
    this.root.removeFromParent()
  }
}
